import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'

const CUISINE_TYPES = [
  'American', 'Chinese', 'Indian', 'Italian', 'Japanese',
  'Korean', 'Mexican', 'Middle Eastern', 'Thai', 'Vietnamese',
  'Mediterranean', 'Other',
]

type Step = 'account' | 'restaurant'

export function SignupPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('account')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    restaurantName: '',
    cuisineType: '',
    city: '',
  })

  function set(field: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(f => ({ ...f, [field]: e.target.value }))
  }

  async function handleAccountNext(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    setStep('restaurant')
  }

  async function handleSignup(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // 1. Create the auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: { data: { full_name: form.fullName } },
      })

      if (authError) throw authError
      if (!authData.user) throw new Error('Signup failed — no user returned')

      // 2. Create restaurant + owner profile via RPC
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: setupError } = await (supabase.rpc as any)('provision_restaurant', {
        p_user_id:        authData.user.id,
        p_full_name:      form.fullName,
        p_restaurant_name: form.restaurantName,
        p_cuisine_type:   form.cuisineType || null,
        p_city:           form.city || null,
      })

      if (setupError) throw setupError

      // If email confirmation is required Supabase won't have a session yet
      if (authData.session) {
        navigate('/dashboard')
      } else {
        navigate('/verify-email')
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4 py-12">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <div className="h-9 w-9 rounded-xl bg-brand-500 flex items-center justify-center">
            <span className="text-white font-bold text-base">R</span>
          </div>
          <span className="font-semibold text-stone-900 text-xl tracking-tight">RezzOps</span>
        </div>

        <div className="card p-6">
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-6">
            <div className={`h-1.5 flex-1 rounded-full transition-colors ${step === 'account' ? 'bg-brand-500' : 'bg-brand-500'}`} />
            <div className={`h-1.5 flex-1 rounded-full transition-colors ${step === 'restaurant' ? 'bg-brand-500' : 'bg-stone-200'}`} />
          </div>

          {step === 'account' ? (
            <>
              <h1 className="text-lg font-semibold text-stone-900 mb-1">Create your account</h1>
              <p className="text-sm text-stone-500 mb-6">Step 1 of 2 — your login details</p>

              <form onSubmit={handleAccountNext} className="space-y-4">
                <Input
                  label="Your name"
                  required
                  autoComplete="name"
                  value={form.fullName}
                  onChange={set('fullName')}
                  placeholder="Rosa Martinez"
                />
                <Input
                  label="Email"
                  type="email"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="you@yourrestaurant.com"
                />
                <Input
                  label="Password"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={form.password}
                  onChange={set('password')}
                  placeholder="Min. 8 characters"
                  hint="At least 8 characters"
                />

                {error && (
                  <p className="text-xs text-danger bg-danger-light rounded px-3 py-2">{error}</p>
                )}

                <Button type="submit" className="w-full">
                  Continue
                </Button>
              </form>
            </>
          ) : (
            <>
              <h1 className="text-lg font-semibold text-stone-900 mb-1">Your restaurant</h1>
              <p className="text-sm text-stone-500 mb-6">Step 2 of 2 — tell us about your place</p>

              <form onSubmit={handleSignup} className="space-y-4">
                <Input
                  label="Restaurant name"
                  required
                  value={form.restaurantName}
                  onChange={set('restaurantName')}
                  placeholder="Rosa's Kitchen"
                />

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-stone-700">
                    Cuisine type <span className="text-stone-400">(optional)</span>
                  </label>
                  <select
                    value={form.cuisineType}
                    onChange={set('cuisineType')}
                    className="flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="">Select cuisine...</option>
                    {CUISINE_TYPES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <Input
                  label="City"
                  value={form.city}
                  onChange={set('city')}
                  placeholder="Austin, TX"
                  hint="Optional — helps with local search later"
                />

                {error && (
                  <p className="text-xs text-danger bg-danger-light rounded px-3 py-2">{error}</p>
                )}

                <Button type="submit" className="w-full" loading={loading}>
                  Create my restaurant
                </Button>

                <button
                  type="button"
                  onClick={() => { setStep('account'); setError('') }}
                  className="w-full text-sm text-stone-500 hover:text-stone-700"
                >
                  ← Back
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-sm text-stone-500 mt-4">
          Already have an account?{' '}
          <Link to="/login" className="text-brand-600 hover:underline font-medium">Sign in</Link>
        </p>
      </div>
    </div>
  )
}
